/*
 * /anchor-watch — расчёт якорной стоянки и якорная вахта.
 *
 * ⛔ Здесь НЕТ и не может быть суточного лимита: это сигнализация. Отказ по
 *    капу у якорной вахты — не воронка, а происшествие.
 *
 * ⚠️ И здесь же честность, которой нет у большинства приложений: вкладка
 *    браузера НЕ гарантирует работы в фоне. Операционная система телефона
 *    может усыпить фоновую вкладку в любой момент. Поэтому страница держит
 *    экран включённым через Wake Lock, ГРОМКО говорит, что её нельзя
 *    сворачивать, и отдельно сообщает, если отсчёты перестали приходить.
 */

import {
    compute, watchState, averagePosition, metersBetween, bearingBetween,
    SCOPE_RATIOS, CHAIN_KG_PER_M, fmtM,
} from './engine.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__AW || { T: {}, cfg: {} };
const T = B.T;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };

const S = {
    mode: 'calc',
    calc: null,
    anchor: null,
    radius: 50,
    fixes: [],
    settling: null,
    watchId: null,
    alarm: null,
    alarmActive: false,
    lastFixAt: 0,
    staleTimer: null,
    map: null, circle: null, marker: null, track: null, boat: null,
};

/* ═══ РАСЧЁТ ═════════════════════════════════════════════════════════════ */
function readCalc() {
    return {
        sounder: Number($('awSounder').value) || 0,
        draft: Number($('awDraft').value) || 0,
        bowRoller: Number($('awBow').value) || 0,
        tideRise: Number($('awTide').value) || 0,
        sounderIncludesDraft: $('awSounderMode').value === 'surface',
        loa: Number($('awLoa').value) || 10,
        boatType: $('awType').value,
        chainMm: Number($('awChain').value) || 10,
        windKn: Number($('awWind').value) || 20,
        ratio: Number($('awRatio').value) || 5,
        rode: Number($('awRode').value) || 0,
        rodeAvailable: Number($('awRodeHave').value) || 0,
    };
}

function renderCalc() {
    const opts = readCalc();
    const r = S.calc = compute(opts);
    const box = $('awOut');
    box.textContent = '';

    const facts = el('div', 'aw-facts');
    const add = (l, v, sub, state) => {
        const f = el('div', 'aw-fact' + (state ? ' ' + state : ''));
        f.append(el('div', 'aw-fact-l', l), el('div', 'aw-fact-v', v));
        if (sub) f.append(el('div', 'aw-fact-s', sub));
        facts.append(f);
    };
    add(t('f_depth'), fmtM(r.depth) + ' ' + t('unit_m'), t('f_depth_sub'));
    add(t('f_rode'), fmtM(r.rode) + ' ' + t('unit_m'),
        t('f_rode_sub', { n: r.ratio.toFixed(1) }),
        r.ratio < 3 ? 'bad' : r.ratio < 4 ? 'warn' : 'good');
    add(t('f_swing'), fmtM(r.swingRadius) + ' ' + t('unit_m'), t('f_swing_sub'));
    add(t('f_load'), Math.round(r.load / 9.81) + ' ' + t('unit_kgf'),
        t('f_load_sub', { wind: opts.windKn }));
    add(t('f_onbottom'), fmtM(r.catenary.onBottom) + ' ' + t('unit_m'),
        r.catenary.lifted ? t('f_onbottom_none') : t('f_onbottom_sub'),
        r.catenary.lifted ? 'bad' : 'good');
    box.append(facts);

    for (const n of r.notes) {
        const p = el('p', 'aw-warn', t('note_' + n, {
            need: fmtM(r.rodeForZeroAngle), have: fmtM(r.rode),
        }));
        if (p.textContent && !p.textContent.startsWith('note_')) box.append(p);
    }

    // Таблица рекомендуемых длин — практика, а не норма, и так и подписана.
    const tbl = el('table', 'aw-table');
    const head = el('tr');
    head.append(el('th', null, t('col_cond')), el('th', null, t('col_ratio')), el('th', null, t('col_rode')));
    tbl.append(head);
    for (const [k, ratio] of Object.entries(SCOPE_RATIOS)) {
        const tr = el('tr');
        if (Math.abs(ratio - opts.ratio) < 0.01) tr.className = 'on';
        tr.append(el('td', null, t('cond_' + k)));
        tr.append(el('td', 'num', ratio + ' : 1'));
        tr.append(el('td', 'num', fmtM(r.recommended[k]) + ' ' + t('unit_m')));
        tbl.append(tr);
    }
    box.append(tbl);
    box.append(el('p', 'aw-note', t('ratios_note')));

    $('awRadius').value = Math.round(r.swingRadius);
    S.radius = Math.round(r.swingRadius);
    drawScheme(r, opts);
}

/** Схема в разрезе: клюз, цепь, грунт, круг. */
function drawScheme(r, opts) {
    const canvas = $('awScheme');
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 600, H = canvas.clientHeight || 220;
    if (canvas.width !== Math.round(W * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const pad = 22;
    const spanX = Math.max(r.horiz * 1.15, 10);
    const spanY = Math.max(r.depth * 1.3, 5);
    const sx = (W - pad * 2) / spanX;
    const sy = (H - pad * 2) / spanY;
    const sc = Math.min(sx, sy);
    const x0 = pad, waterY = pad + (opts.bowRoller || 0) * sc;
    const bedY = waterY + (r.depth - (opts.bowRoller || 0)) * sc;

    // Вода и грунт.
    ctx.fillStyle = 'rgba(90,150,200,.16)';
    ctx.fillRect(0, waterY, W, bedY - waterY);
    ctx.fillStyle = 'rgba(150,130,90,.35)';
    ctx.fillRect(0, bedY, W, H - bedY);
    ctx.strokeStyle = 'rgba(140,180,220,.5)'; ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(0, waterY); ctx.lineTo(W, waterY); ctx.stroke();

    // Цепь: часть на грунте, часть цепной линией.
    const anchorX = x0, bowX = x0 + r.horiz * sc;
    const onBottomPx = Math.min(r.catenary.onBottom, r.horiz) * sc;
    ctx.strokeStyle = '#e0a458'; ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.moveTo(anchorX, bedY);
    ctx.lineTo(anchorX + onBottomPx, bedY);
    // Приблизительная дуга до клюза.
    ctx.quadraticCurveTo((anchorX + onBottomPx + bowX) / 2, bedY, bowX, pad);
    ctx.stroke();

    // Якорь и судно.
    ctx.fillStyle = '#d95c48';
    ctx.beginPath(); ctx.arc(anchorX, bedY, 5, 0, 7); ctx.fill();
    ctx.fillStyle = '#e8eef4';
    ctx.beginPath();
    ctx.moveTo(bowX - 16, waterY); ctx.lineTo(bowX + 22, waterY);
    ctx.lineTo(bowX + 16, waterY + 7); ctx.lineTo(bowX - 10, waterY + 7);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = '#e8eef4'; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(bowX - 16, waterY); ctx.lineTo(bowX - 16, pad); ctx.stroke();

    ctx.fillStyle = 'rgba(190,210,235,.85)';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(t('scheme_depth', { n: fmtM(r.depth) }), W - 130, waterY + 16);
    ctx.fillText(t('scheme_horiz', { n: fmtM(r.horiz) }), W - 130, waterY + 32);
}

/* ═══ ВАХТА ══════════════════════════════════════════════════════════════ */
function initMap() {
    if (!window.L || S.map) return;
    S.map = L.map('awMap', { zoomControl: true }).setView([43.2, 16.4], 16);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(S.map);
}

function updateMap() {
    if (!S.map) return;
    if (S.anchor) {
        if (!S.marker) S.marker = L.circleMarker([S.anchor.lat, S.anchor.lon], { radius: 6, color: '#d95c48', fillOpacity: 1 }).addTo(S.map);
        else S.marker.setLatLng([S.anchor.lat, S.anchor.lon]);
        if (!S.circle) S.circle = L.circle([S.anchor.lat, S.anchor.lon], { radius: S.radius, color: '#6366f1', weight: 2, fillOpacity: 0.06 }).addTo(S.map);
        else { S.circle.setLatLng([S.anchor.lat, S.anchor.lon]); S.circle.setRadius(S.radius); }
    }
    const pts = S.fixes.slice(-400).map(f => [f.lat, f.lon]);
    if (pts.length) {
        if (!S.track) S.track = L.polyline(pts, { color: '#22c55e', weight: 2, opacity: .8 }).addTo(S.map);
        else S.track.setLatLngs(pts);
        const last = pts[pts.length - 1];
        if (!S.boat) S.boat = L.circleMarker(last, { radius: 5, color: '#22c55e', fillOpacity: 1 }).addTo(S.map);
        else S.boat.setLatLng(last);
    }
}

function startWatch() {
    if (!navigator.geolocation) { window.__toast?.(t('geo_unavailable'), 'error'); return; }
    if (S.watchId !== null) return;
    S.watchId = navigator.geolocation.watchPosition(onFix, onGeoError, {
        enableHighAccuracy: true, maximumAge: 2000, timeout: 30000,
    });
    requestWakeLock();
    $('awStart').hidden = true;
    $('awStop').hidden = false;
    S.staleTimer = setInterval(checkStale, 5000);
    window.__stage?.('watch_started');
}

function stopWatch() {
    if (S.watchId !== null) navigator.geolocation.clearWatch(S.watchId);
    S.watchId = null;
    clearInterval(S.staleTimer);
    stopAlarm();
    releaseWakeLock();
    $('awStart').hidden = false;
    $('awStop').hidden = true;
    setStatus('idle');
}

function onFix(pos) {
    const f = {
        lat: pos.coords.latitude, lon: pos.coords.longitude,
        accuracy: pos.coords.accuracy, at: Date.now(),
    };
    S.lastFixAt = f.at;
    S.fixes.push(f);
    if (S.fixes.length > 2000) S.fixes.splice(0, 500);

    if (S.settling) {
        S.settling.push(f);
        $('awSettle').textContent = t('settling', { n: S.settling.length, need: 5 });
        if (S.settling.length >= 5) {
            S.anchor = averagePosition(S.settling);
            S.settling = null;
            $('awSettle').textContent = '';
            saveAnchor();
            if (S.map) S.map.setView([S.anchor.lat, S.anchor.lon], 17);
        }
    }
    render();
}

function onGeoError(err) {
    setStatus('geo-error', { msg: err.code === 1 ? t('geo_denied') : t('geo_error') });
}

function checkStale() {
    if (S.watchId === null) return;
    const age = (Date.now() - S.lastFixAt) / 1000;
    $('awStale').hidden = age < 45 || !S.lastFixAt;
    if (age >= 45 && S.lastFixAt) $('awStale').textContent = t('stale', { n: Math.round(age) });
}

function render() {
    const st = watchState(S.anchor, S.fixes, S.radius);
    setStatus(st.state, st);
    updateMap();
    if (st.state === 'alarm') startAlarm(); else if (S.alarmActive && st.state === 'ok') stopAlarm();
}

function setStatus(state, st = {}) {
    const box = $('awStatus');
    box.className = 'aw-status ' + state;
    box.textContent = '';
    const label = {
        idle: t('st_idle'), ok: t('st_ok'), warn: t('st_warn'),
        alarm: t('st_alarm'), 'geo-error': st.msg || t('geo_error'),
    }[state] || state;
    box.append(el('div', 'aw-status-h', label));
    if (Number.isFinite(st.dist)) {
        box.append(el('div', 'aw-status-d', t('st_detail', {
            dist: fmtM(st.dist), radius: fmtM(S.radius),
            brg: Math.round(st.bearing), acc: st.accuracy != null ? Math.round(st.accuracy) : '—',
        })));
    }
    if (state === 'alarm') {
        const b = el('button', 'aw-btn danger');
        b.type = 'button'; b.textContent = t('btn_silence');
        b.addEventListener('click', stopAlarm);
        box.append(b);
    }
}

/* ── тревога ─────────────────────────────────────────────────────────────── */
function startAlarm() {
    if (S.alarmActive) return;
    S.alarmActive = true;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ac = S.alarm?.ctx || new AC();
        if (ac.state === 'suspended') ac.resume().catch(() => {});
        const gain = ac.createGain();
        gain.gain.value = 0.0001;
        gain.connect(ac.destination);
        const osc = ac.createOscillator();
        osc.type = 'square';
        osc.connect(gain);
        osc.start();
        // Двухтональный сигнал: он пробивается сквозь шум ветра и такелажа
        // лучше ровного тона и не спутывается с телефонным звонком.
        let on = false;
        const tick = setInterval(() => {
            on = !on;
            const now = ac.currentTime;
            osc.frequency.setValueAtTime(on ? 880 : 1320, now);
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(gain.gain.value, now);
            gain.gain.linearRampToValueAtTime(0.35, now + 0.02);
            gain.gain.setValueAtTime(0.35, now + 0.28);
            gain.gain.linearRampToValueAtTime(0.0001, now + 0.34);
            navigator.vibrate?.([250, 120]);
        }, 500);
        S.alarm = { ctx: ac, osc, gain, tick };
    } catch { /* аудио недоступно — визуальная тревога остаётся */ }
    document.body.classList.add('aw-alarming');
    window.__announce?.(t('st_alarm'));
    window.__aeCritical?.('anchor_drag', { radius: S.radius });
}

function stopAlarm() {
    S.alarmActive = false;
    document.body.classList.remove('aw-alarming');
    if (!S.alarm) return;
    clearInterval(S.alarm.tick);
    try {
        S.alarm.gain.gain.cancelScheduledValues(S.alarm.ctx.currentTime);
        S.alarm.gain.gain.setValueAtTime(0.0001, S.alarm.ctx.currentTime);
        S.alarm.osc.stop(S.alarm.ctx.currentTime + 0.05);
    } catch { /* уже остановлен */ }
    S.alarm = { ctx: S.alarm.ctx };
}

/* ── экран не должен гаснуть ─────────────────────────────────────────────── */
/*
 * ⚠️⚠️ Здесь стояла своя копия запроса экранного лока с проверкой
 * «объект есть — значит держим». Ровно эта проверка и есть измеренный дефект:
 * после
 * freeze/bfcache событие `release` НЕ приходит, объект остаётся правдивым, и
 * страница считает экран удержанным, пока он гаснет (docs/wake-lock-failure-
 * modes.md). Для якорной вахты это не мелочь: она работает ночь напролёт на
 * телефоне, то есть живёт ровно в том сценарии, ради которого примитив и
 * написан — он перепроверяет удержание сторожевым таймером, перебирает лок
 * после возврата из фона и падает на видео-фолбэк там, где API отказал.
 */
async function requestWakeLock() {
    const how = await (window.__wakeLock
        ? window.__wakeLock.request()
        : Promise.resolve('none'));
    $('awNoWake').hidden = how !== 'none';
}
function releaseWakeLock() { try { window.__wakeLock?.release(); } catch { /* нечего отпускать */ } }
document.addEventListener('visibilitychange', () => {
    // Примитив перебирает лок сам; нам остаётся честно показать состояние.
    if (document.visibilityState === 'visible' && S.watchId !== null) requestWakeLock();
});

/* ── хранение ────────────────────────────────────────────────────────────── */
function saveAnchor() {
    try { window.__toolSettings?.save('anchor-watch', 'anchor', { anchor: S.anchor, radius: S.radius }); } catch {}
}
function restoreAnchor() {
    try {
        const v = window.__toolSettings?.load('anchor-watch', 'anchor', null);
        if (v?.anchor && Number.isFinite(v.anchor.lat)) { S.anchor = v.anchor; S.radius = v.radius || 50; }
    } catch {}
}

/* ── подключение ─────────────────────────────────────────────────────────── */
function setMode(m) {
    S.mode = m;
    for (const b of document.querySelectorAll('.aw-tab')) b.classList.toggle('on', b.dataset.mode === m);
    for (const p of document.querySelectorAll('.aw-pane')) p.hidden = p.dataset.mode !== m;
    if (m === 'watch') { initMap(); setTimeout(() => { S.map?.invalidateSize(); updateMap(); }, 60); }
    if (m === 'calc') renderCalc();
}

function bind() {
    for (const b of document.querySelectorAll('.aw-tab')) b.addEventListener('click', () => setMode(b.dataset.mode));
    for (const id of ['awSounder', 'awDraft', 'awBow', 'awTide', 'awLoa', 'awChain', 'awWind', 'awRatio', 'awRode', 'awRodeHave']) {
        $(id).addEventListener('input', () => { clearTimeout(bind._d); bind._d = setTimeout(renderCalc, 160); });
    }
    for (const id of ['awSounderMode', 'awType']) $(id).addEventListener('change', renderCalc);

    $('awSetAnchor').addEventListener('click', () => {
        if (!navigator.geolocation) { window.__toast?.(t('geo_unavailable'), 'error'); return; }
        S.settling = [];
        $('awSettle').textContent = t('settling', { n: 0, need: 5 });
        if (S.watchId === null) startWatch();
    });
    $('awRadius').addEventListener('input', e => {
        S.radius = Math.max(5, Number(e.target.value) || 5);
        saveAnchor(); updateMap(); render();
    });
    $('awStart').addEventListener('click', startWatch);
    $('awStop').addEventListener('click', stopWatch);
    $('awTest').addEventListener('click', () => {
        // Проверка сигнала — обязательная кнопка: тревогу, которую ни разу не
        // слышали, ночью принимают за телефон и выключают.
        startAlarm();
        setTimeout(stopAlarm, 2500);
    });
}

function boot() {
    try {
        bind();
        restoreAnchor();
        renderCalc();
        setMode('calc');
        if (S.anchor) $('awRadius').value = S.radius;
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'anchor-watch');
        const b = $('awOut');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
